import { supabase } from "@/services/supabase/client";
import { Database } from "@/services/supabase/types";
import { loadKeyFromDB } from "@/features/encryption/utils/storage";
import { processTodoContent, prepareEncryptedTodoContent } from "../utils/todo-encryption";

type DbTodos = Database["public"]["Tables"]["todos"];
export type TodoRow = DbTodos["Row"];
export type TodoInsert = DbTodos["Insert"];
export type TodoUpdate = DbTodos["Update"];

async function fetchTodos(): Promise<TodoRow[]> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayISO = today.toISOString();

  // Fetch both active todos and completed todos from today
  const { data, error } = await supabase
    .from("todos")
    .select("*")
    .or(`done.eq.false,and(done.eq.true,done_at.gte.${todayISO})`)
    .order("position", { ascending: true });

  if (error) {
    throw error;
  }

  const dek = await loadKeyFromDB();

  return Promise.all(
    data.map(async (todo) => {
      const { text } = await processTodoContent(todo, dek);

      return {
        ...todo,
        text,
      };
    })
  );
}

async function addTodo(todo: TodoInsert): Promise<TodoRow> {
  const dek = await loadKeyFromDB();
  const { encrypted_data, iv, clearContent } = await prepareEncryptedTodoContent(todo.text, dek);

  const insertData = {
    ...todo,
    ...(clearContent && encrypted_data && iv ? { encrypted_data, iv, text: "" } : { encrypted_data: "", iv: "" }),
  };

  const { data, error } = await supabase.from("todos").insert(insertData).select().single();
  if (error) throw error;

  return { ...data, text: todo.text };
}

async function updateTodo({ id, todo }: { id: number; todo: TodoUpdate }): Promise<TodoRow> {
  const dek = await loadKeyFromDB();

  // Prepare encryption if text is being updated
  if (todo.text) {
    const { encrypted_data, iv, clearContent } = await prepareEncryptedTodoContent(todo.text, dek);

    if (clearContent && encrypted_data && iv) {
      todo.encrypted_data = encrypted_data;
      todo.iv = iv;
      todo.text = "";
    }
  }

  const { data, error } = await supabase.from("todos").update(todo).eq("id", id).select().single();
  if (error) throw error;

  return data;
}

async function deleteTodo(id: number) {
  const { data, error } = await supabase.from("todos").delete().eq("id", id);
  if (error) throw error;

  return data;
}

async function fetchCompletedTodos(): Promise<TodoRow[]> {
  const { data, error } = await supabase
    .from("todos")
    .select("*")
    .eq("done", true)
    .order("done_at", { ascending: false });

  if (error) {
    throw error;
  }

  const dek = await loadKeyFromDB();

  return Promise.all(
    data.map(async (todo) => {
      const { text } = await processTodoContent(todo, dek);

      return {
        ...todo,
        text,
      };
    })
  );
}

export const todosFetchers = {
  fetchTodos,
  addTodo,
  updateTodo,
  deleteTodo,
  fetchCompletedTodos,
};
